<template>
  <div class="range-explorer-preview mt-4 p-3 bg-base-200 rounded-lg">
    <div class="flex items-center justify-between mb-3">
      <h4 class="font-semibold text-sm">
        Range Explorer Preview
      </h4>
      <div class="flex items-center gap-1">
        <button
          v-if="resultData && !isLoading"
          type="button"
          class="btn btn-ghost btn-xs"
          title="Fullscreen chart"
          @click="openFullscreen"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="1.5"
            stroke="currentColor"
            class="w-4 h-4"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15"
            />
          </svg>
        </button>
        <button
          v-if="!collapsed"
          type="button"
          class="btn btn-ghost btn-xs"
          @click="collapsed = true"
        >
          Collapse
        </button>
        <button
          v-else
          type="button"
          class="btn btn-ghost btn-xs"
          @click="collapsed = false"
        >
          Expand
        </button>
      </div>
    </div>

    <div v-show="!collapsed">
      <!-- Controls Row -->
      <div class="flex flex-wrap gap-2 mb-3 items-end">
        <!-- Trading Mode -->
        <div class="form-control">
          <label class="label py-0">
            <span class="label-text text-xs">Mode</span>
          </label>
          <div class="join">
            <button
              type="button"
              class="join-item btn btn-xs"
              :class="localTradingMode === 'LONG' ? 'btn-success' : 'btn-ghost'"
              @click="localTradingMode = 'LONG'"
            >
              LONG
            </button>
            <button
              type="button"
              class="join-item btn btn-xs"
              :class="localTradingMode === 'SHORT' ? 'btn-error' : 'btn-ghost'"
              @click="localTradingMode = 'SHORT'"
            >
              SHORT
            </button>
          </div>
        </div>

        <!-- Lookback Period -->
        <div class="form-control">
          <label class="label py-0">
            <span class="label-text text-xs">Lookback</span>
          </label>
          <div class="join">
            <button
              v-for="period in lookbackPeriods"
              :key="period.value"
              type="button"
              class="join-item btn btn-xs"
              :class="{ 'btn-active btn-primary': lookbackPeriod === period.value }"
              @click="lookbackPeriod = period.value"
            >
              {{ period.label }}
            </button>
          </div>
        </div>

        <!-- Timeframe -->
        <div class="form-control">
          <label class="label py-0">
            <span class="label-text text-xs">Timeframe</span>
          </label>
          <div class="join">
            <button
              v-for="tf in timeframes"
              :key="tf.value"
              type="button"
              class="join-item btn btn-xs"
              :class="{ 'btn-active btn-primary': timeframe === tf.value }"
              @click="timeframe = tf.value"
            >
              {{ tf.label }}
            </button>
          </div>
        </div>
      </div>

      <!-- Second Row: Grid Parameters -->
      <div class="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
        <!-- Total Levels -->
        <div class="form-control">
          <label class="label py-0">
            <span class="label-text text-xs">Levels</span>
          </label>
          <div class="flex items-center gap-1">
            <input
              v-model.number="totalLevels"
              type="number"
              min="10"
              max="1000"
              step="1"
              class="input input-bordered input-xs w-16"
            >
            <input
              v-model.number="totalLevels"
              type="range"
              min="10"
              max="500"
              step="10"
              class="range range-xs flex-1"
            >
          </div>
        </div>

        <!-- Profit Ratio -->
        <div class="form-control">
          <label class="label py-0">
            <span class="label-text text-xs">Profit Ratio</span>
          </label>
          <div class="flex items-center gap-1">
            <input
              v-model.number="profitRatio"
              type="number"
              min="1"
              max="100"
              step="0.1"
              class="input input-bordered input-xs w-16"
            >
            <input
              v-model.number="profitRatio"
              type="range"
              min="2"
              max="20"
              step="1"
              class="range range-xs flex-1"
            >
          </div>
        </div>

        <!-- Range Multiplier -->
        <div class="form-control">
          <label class="label py-0">
            <span class="label-text text-xs">Range Mult</span>
          </label>
          <div class="flex items-center gap-1">
            <input
              v-model.number="rangeMultiplier"
              type="number"
              min="0.5"
              max="5"
              step="0.1"
              class="input input-bordered input-xs w-16"
            >
            <input
              v-model.number="rangeMultiplier"
              type="range"
              min="1.0"
              max="3.0"
              step="0.1"
              class="range range-xs flex-1"
            >
          </div>
        </div>

        <!-- Capital (USD) with log scale -->
        <div class="form-control">
          <label class="label py-0">
            <span class="label-text text-xs">Capital ($)</span>
          </label>
          <div class="flex items-center gap-1">
            <div class="relative">
              <span class="absolute left-1 top-1/2 -translate-y-1/2 text-xs text-base-content/70">$</span>
              <input
                :value="capitalUSD.toLocaleString()"
                type="text"
                class="input input-bordered input-xs w-20 pl-3"
                @change="updateCapitalFromInput($event.target.value)"
              >
            </div>
            <input
              :value="capitalLogSlider"
              type="range"
              min="1"
              max="7"
              step="0.01"
              class="range range-xs flex-1"
              @input="updateCapitalFromLog($event.target.value)"
            >
          </div>
        </div>
      </div>

      <!-- Sizing Mode -->
      <div class="flex flex-wrap items-center gap-2 mb-3">
        <span class="text-xs font-semibold">Sizing:</span>
        <div class="join">
          <button
            v-for="mode in sizingModes"
            :key="mode.value"
            type="button"
            class="join-item btn btn-xs"
            :class="{ 'btn-active btn-primary': sizingMode === mode.value }"
            @click="sizingMode = mode.value"
          >
            {{ mode.label }}
          </button>
        </div>

        <!-- Non-flat controls inline -->
        <template v-if="sizingMode !== 'flat'">
          <div class="flex items-center gap-1">
            <span class="text-xs text-base-content/70">Max mult:</span>
            <input
              v-model.number="maxOrderSizeMultiplier"
              type="number"
              min="1"
              max="10"
              step="0.1"
              class="input input-bordered input-xs w-14"
            >
          </div>
          <div class="flex items-center gap-1">
            <span class="text-xs text-base-content/70">Spacing:</span>
            <input
              v-model.number="spacingFactor"
              type="number"
              min="1"
              max="5"
              step="0.1"
              class="input input-bordered input-xs w-14"
            >
          </div>
          <div
            v-if="sizingMode === 'geometric'"
            class="flex items-center gap-1"
          >
            <span class="text-xs text-base-content/70">Curve:</span>
            <input
              v-model.number="geometricScalingCurve"
              type="number"
              min="0.5"
              max="3"
              step="0.1"
              class="input input-bordered input-xs w-14"
            >
          </div>
        </template>
      </div>

      <!-- Error -->
      <div
        v-if="error"
        class="alert alert-error alert-sm mb-3 py-1 text-xs"
      >
        <span>{{ error }}</span>
      </div>

      <!-- Order Size Warning -->
      <OrderSizeWarning
        v-if="resultData && orderSizeUsd !== null"
        :order-size-usd="orderSizeUsd"
        class="mb-3"
      />

      <!-- Validation Warnings -->
      <div
        v-if="validationWarning"
        class="alert alert-warning alert-sm mb-3 py-1 text-xs"
      >
        <span>{{ validationWarning }}</span>
      </div>

      <!-- Loading -->
      <div
        v-if="isLoading"
        class="flex items-center justify-center py-8"
      >
        <span class="loading loading-spinner loading-md" />
        <span class="ml-2 text-sm">Calculating grid...</span>
      </div>

      <!-- Results -->
      <template v-if="resultData && !isLoading">
        <!-- Stats Cards -->
        <div
          v-if="!hideStats"
          class="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3"
        >
          <div class="bg-base-100 rounded p-2">
            <div class="text-xs text-base-content/60">
              Current Price
            </div>
            <div class="text-sm font-semibold text-primary">
              ${{ formatPrice(resultData.stats.current_price) }}
            </div>
          </div>
          <div class="bg-base-100 rounded p-2">
            <div class="text-xs text-base-content/60">
              Period High / Low
            </div>
            <div class="text-sm font-semibold">
              ${{ formatPrice(resultData.stats.period_high) }} / ${{ formatPrice(resultData.stats.period_low) }}
            </div>
          </div>
          <div class="bg-base-100 rounded p-2">
            <div class="text-xs text-base-content/60">
              Grid Range
            </div>
            <div class="text-sm font-semibold">
              ${{ formatPrice(resultData.stats.grid_range) }}
            </div>
          </div>
          <div class="bg-base-100 rounded p-2">
            <div class="text-xs text-base-content/60">
              Entry / Exit Spacing
            </div>
            <div class="text-sm font-semibold">
              ${{ formatPrice(resultData.grid_config.entry_spacing_dollars) }} / ${{ formatPrice(resultData.grid_config.exit_spacing_dollars) }}
            </div>
          </div>
        </div>

        <!-- Chart -->
        <div
          ref="chartContainer"
          class="chart-container bg-base-200 rounded mb-3"
        />

        <!-- Config Output -->
        <div class="collapse collapse-arrow bg-base-300 rounded">
          <input
            v-model="configExpanded"
            type="checkbox"
            class="peer"
          >
          <div class="collapse-title text-xs font-medium py-1 min-h-0">
            Grid Config Output
          </div>
          <div class="collapse-content">
            <div class="relative">
              <pre class="bg-base-100 p-2 rounded text-xs overflow-x-auto"><code>{{ JSON.stringify(resultData.grid_config, null, 2) }}</code></pre>
              <button
                type="button"
                class="absolute top-1 right-1 btn btn-ghost btn-xs"
                title="Copy to clipboard"
                @click="copyConfig"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width="1.5"
                  stroke="currentColor"
                  class="w-3 h-3"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M15.666 3.888A2.25 2.25 0 0 0 13.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 0 1-.75.75H9.75a.75.75 0 0 1-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 0 1-2.25 2.25H6.75A2.25 2.25 0 0 1 4.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 0 1 1.927-.184"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </template>
    </div>

    <!-- Fullscreen Modal -->
    <teleport to="body">
      <div
        v-if="maximized"
        class="modal modal-open"
        style="z-index: 1100;"
      >
        <div class="modal-box max-w-7xl w-[95vw] h-[90vh] flex flex-col">
          <div class="flex items-center justify-between mb-3">
            <h3 class="font-semibold text-lg">
              Range Explorer - {{ apiSymbol }} ({{ localTradingMode }})
            </h3>
            <button
              type="button"
              class="btn btn-sm btn-circle btn-ghost"
              @click="closeFullscreen"
            >
              ✕
            </button>
          </div>

          <!-- Fullscreen Stats Cards -->
          <div
            v-if="resultData && !hideStats"
            class="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3"
          >
            <div class="bg-base-200 rounded p-2">
              <div class="text-xs text-base-content/60">
                Current Price
              </div>
              <div class="text-sm font-semibold text-primary">
                ${{ formatPrice(resultData.stats.current_price) }}
              </div>
            </div>
            <div class="bg-base-200 rounded p-2">
              <div class="text-xs text-base-content/60">
                Period High / Low
              </div>
              <div class="text-sm font-semibold">
                ${{ formatPrice(resultData.stats.period_high) }} / ${{ formatPrice(resultData.stats.period_low) }}
              </div>
            </div>
            <div class="bg-base-200 rounded p-2">
              <div class="text-xs text-base-content/60">
                Grid Range
              </div>
              <div class="text-sm font-semibold">
                ${{ formatPrice(resultData.stats.grid_range) }}
              </div>
            </div>
            <div class="bg-base-200 rounded p-2">
              <div class="text-xs text-base-content/60">
                Entry / Exit Spacing
              </div>
              <div class="text-sm font-semibold">
                ${{ formatPrice(resultData.grid_config.entry_spacing_dollars) }} / ${{ formatPrice(resultData.grid_config.exit_spacing_dollars) }}
              </div>
            </div>
          </div>

          <!-- Fullscreen Chart -->
          <div
            ref="fullscreenChartContainer"
            class="fullscreen-chart-container bg-base-200 rounded flex-1 min-h-0"
          />

          <!-- Fullscreen Config Output -->
          <div
            v-if="resultData"
            class="collapse collapse-arrow bg-base-300 rounded mt-3"
          >
            <input
              type="checkbox"
              class="peer"
            >
            <div class="collapse-title text-xs font-medium py-1 min-h-0">
              Grid Config Output
            </div>
            <div class="collapse-content">
              <div class="relative">
                <pre class="bg-base-100 p-2 rounded text-xs overflow-x-auto"><code>{{ JSON.stringify(resultData.grid_config, null, 2) }}</code></pre>
                <button
                  type="button"
                  class="absolute top-1 right-1 btn btn-ghost btn-xs"
                  title="Copy to clipboard"
                  @click="copyConfig"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke-width="1.5"
                    stroke="currentColor"
                    class="w-3 h-3"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M15.666 3.888A2.25 2.25 0 0 0 13.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 0 1-.75.75H9.75a.75.75 0 0 1-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 0 1-2.25 2.25H6.75A2.25 2.25 0 0 1 4.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 0 1 1.927-.184"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
        <div
          class="modal-backdrop"
          @click="closeFullscreen"
        />
      </div>
    </teleport>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';
import Plotly from 'plotly.js-dist-min';
import api from '@/api/axios';
import OrderSizeWarning from '@/components/OrderSizeWarning.vue';

const props = defineProps({
  symbol: {
    type: String,
    default: '',
  },
  category: {
    type: String,
    default: 'Linear',
  },
  tradingMode: {
    type: String,
    default: 'LONG',
  },
  initialCapital: {
    type: Number,
    default: 1000,
  },
  defaults: {
    type: Object,
    default: null,
  },
  hideStats: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['config-updated']);

// UI state
const collapsed = ref(false);
const configExpanded = ref(false);
const chartContainer = ref(null);
const fullscreenChartContainer = ref(null);
const maximized = ref(false);
const isLoading = ref(false);
const error = ref(null);
const resultData = ref(null);
const pendingRender = ref(false);

// Parameter state
const localTradingMode = ref(props.tradingMode?.toUpperCase() || 'LONG');
const lookbackPeriod = ref('24h');
const timeframe = ref('1h');
const totalLevels = ref(200);
const profitRatio = ref(10);
const rangeMultiplier = ref(1.2);
const capitalUSD = ref(props.initialCapital || 1000);
const sizingMode = ref('flat');
const maxOrderSizeMultiplier = ref(2.0);
const spacingFactor = ref(2.0);
const geometricScalingCurve = ref(1.0);

// Debounce timer
let debounceTimer = null;
let resizeObserver = null;

// Constants
const lookbackPeriods = [
  { value: '24h', label: '24h' },
  { value: '48h', label: '48h' },
  { value: '72h', label: '72h' },
  { value: '1w', label: '1W' },
  { value: '1m', label: '1M' },
];

const timeframes = [
  { label: '5m', value: '5m' },
  { label: '15m', value: '15m' },
  { label: '1h', value: '1h' },
  { label: '4h', value: '4h' },
];

const sizingModes = [
  { value: 'flat', label: 'Flat' },
  { value: 'linear', label: 'Linear' },
  { value: 'geometric', label: 'Geometric' },
];

// Convert symbol format: "BTC-USDT" -> "BTC/USDT"
const apiSymbol = computed(() => {
  if (!props.symbol) return '';
  return props.symbol.replace('-', '/');
});

// Capital log slider
const capitalLogSlider = computed(() => {
  return Math.log10(capitalUSD.value);
});

// Order size in USD for minimum order validation
const orderSizeUsd = computed(() => {
  if (!resultData.value?.grid_config) return null;
  const orderSize = resultData.value.grid_config.order_size || 0;
  const price = resultData.value.stats?.current_price || 0;
  return orderSize * price;
});

const updateCapitalFromLog = (logValue) => {
  capitalUSD.value = Math.round(Math.pow(10, parseFloat(logValue)));
};

const updateCapitalFromInput = (value) => {
  const parsed = parseFloat(value.replace(/[$,]/g, ''));
  if (!isNaN(parsed) && parsed > 0) {
    capitalUSD.value = parsed;
  }
};

// Format price for display
const formatPrice = (price) => {
  if (typeof price !== 'number' || isNaN(price)) return '0.00';
  if (price >= 1000) {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(price);
  }
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 6,
  }).format(price);
};

// Validation warnings (capital exceeded, order size clamped)
const validationWarning = computed(() => {
  if (!resultData.value) return null;
  const warnings = [];
  const stats = resultData.value.stats;
  const entrySpacing = resultData.value.grid_config.entry_spacing_dollars;

  if (stats.order_size_clamped) {
    warnings.push(`Order size was clamped to minimum $${stats.min_order_usd} USD. Total capital ($${stats.total_capital_usd.toFixed(2)}) exceeds input capital ($${capitalUSD.value.toLocaleString()}). Reduce levels or increase capital.`);
  }

  if (entrySpacing < 0.0001) {
    warnings.push(`Entry spacing ($${entrySpacing.toFixed(6)}) is extremely small. Reduce levels or increase range.`);
  }

  return warnings.length > 0 ? warnings.join(' | ') : null;
});

// Fetch calculation from backend
const fetchCalculation = async (showSpinner = true) => {
  if (!apiSymbol.value) return;

  if (showSpinner) isLoading.value = true;
  error.value = null;

  try {
    const payload = {
      symbol: apiSymbol.value,
      timeframe: timeframe.value,
      lookback_period: lookbackPeriod.value,
      trading_mode: localTradingMode.value,
      capital_usd: capitalUSD.value,
      total_levels: totalLevels.value,
      profit_ratio: profitRatio.value,
      range_multiplier: rangeMultiplier.value,
      sizing_mode: sizingMode.value,
      max_order_size_multiplier: maxOrderSizeMultiplier.value,
      spacing_factor: spacingFactor.value,
      geometric_scaling_curve: geometricScalingCurve.value,
    };

    const response = await api.post('/api/market/range-explorer/calculate/', payload);
    resultData.value = response.data;

    // Emit full result for BotModal integration (grid_config + stats + explorer inputs)
    emit('config-updated', {
      ...response.data,
      _range_explorer: {
        lookback_period: lookbackPeriod.value,
        timeframe: timeframe.value,
        total_levels: totalLevels.value,
        profit_ratio: profitRatio.value,
        range_multiplier: rangeMultiplier.value,
        capital_usd: capitalUSD.value,
        sizing_mode: sizingMode.value,
        max_order_size_multiplier: maxOrderSizeMultiplier.value,
        spacing_factor: spacingFactor.value,
        geometric_scaling_curve: geometricScalingCurve.value,
      },
    });

    // Set isLoading false BEFORE rendering so the v-if template
    // renders the chart container into the DOM
    const wasLoading = isLoading.value;
    isLoading.value = false;

    // Wait for DOM, then render. Use shorter delay when chart is already visible.
    await nextTick();
    setTimeout(() => renderChart(), wasLoading ? 200 : 50);
  } catch (err) {
    console.error('Range Explorer calculation error:', err);
    error.value = err.response?.data?.error || err.response?.data?.detail || err.message || 'Calculation failed';
    isLoading.value = false;
  }
};

// Debounced fetch — light params skip spinner and use longer debounce
const debouncedFetch = (showSpinner = true) => {
  if (debounceTimer) clearTimeout(debounceTimer);
  const delay = showSpinner ? 500 : 800;
  debounceTimer = setTimeout(() => {
    fetchCalculation(showSpinner);
  }, delay);
};

// Get the active chart container based on current mode
const getActiveChartContainer = () => {
  return maximized.value ? fullscreenChartContainer.value : chartContainer.value;
};

// Chart rendering (adapted from RangeExplorerTab.vue)
const renderChart = (targetContainer) => {
  const container = targetContainer || getActiveChartContainer();
  if (!container || !resultData.value) {
    // Container not visible yet -- mark as pending so ResizeObserver can pick it up
    if (resultData.value) {
      pendingRender.value = true;
    }
    return;
  }

  // Check if the container has non-zero dimensions (Plotly requires this)
  if (container.offsetWidth === 0 || container.offsetHeight === 0) {
    pendingRender.value = true;
    return;
  }

  pendingRender.value = false;

  const data = resultData.value;
  const ohlc = data.ohlc_data || [];
  const gridDetail = data.grid_detail || {};
  const entryLevels = gridDetail.entry_levels || [];
  const orderSizes = gridDetail.order_sizes || [];
  const config = data.grid_config || {};
  const stats = data.stats || {};

  if (ohlc.length === 0) return;

  const dates = ohlc.map(c => new Date(c.timestamp));
  const opens = ohlc.map(c => c.open);
  const highs = ohlc.map(c => c.high);
  const lows = ohlc.map(c => c.low);
  const closes = ohlc.map(c => c.close);

  const baseAsset = apiSymbol.value.split('/')[0] || '';

  // Main candlestick trace
  const traces = [
    {
      x: dates,
      open: opens,
      high: highs,
      low: lows,
      close: closes,
      type: 'candlestick',
      increasing: { line: { color: '#26a69a' }, fillcolor: '#26a69a' },
      decreasing: { line: { color: '#ef5350' }, fillcolor: '#ef5350' },
      name: apiSymbol.value,
      xaxis: 'x',
      yaxis: 'y',
    },
  ];

  // Order size bars
  if (entryLevels.length > 0 && orderSizes.length > 0) {
    const step = Math.max(1, Math.floor(entryLevels.length / 50));
    const sampledLevels = [];
    const sampledSizes = [];
    for (let i = 0; i < entryLevels.length; i += step) {
      sampledLevels.push(entryLevels[i]);
      sampledSizes.push(orderSizes[i]);
    }
    traces.push({
      x: sampledSizes,
      y: sampledLevels,
      type: 'bar',
      orientation: 'h',
      xaxis: 'x2',
      yaxis: 'y',
      marker: {
        color: localTradingMode.value === 'LONG' ? '#3b82f6' : '#f97316',
        opacity: 0.7,
      },
      hovertemplate: '%{x:.4f} ' + baseAsset + ' @ $%{y:,.2f}<extra></extra>',
      showlegend: false,
      name: 'Order Size',
    });
  }

  // Shapes
  const shapes = [];

  // Reference price line
  const refPrice = config.reference_price || stats.current_price;
  if (refPrice) {
    shapes.push({
      type: 'line',
      xref: 'paper',
      x0: 0,
      x1: 0.85,
      yref: 'y',
      y0: refPrice,
      y1: refPrice,
      line: {
        color: localTradingMode.value === 'LONG' ? '#22c55e' : '#ef4444',
        width: 3,
        dash: 'solid',
      },
    });
  }

  // Grid boundary line
  const boundaryPrice = localTradingMode.value === 'LONG'
    ? config.range_floor_price
    : config.range_ceiling_price;
  if (boundaryPrice) {
    shapes.push({
      type: 'line',
      xref: 'paper',
      x0: 0,
      x1: 0.85,
      yref: 'y',
      y0: boundaryPrice,
      y1: boundaryPrice,
      line: {
        color: '#6b7280',
        width: 2,
        dash: 'dash',
      },
    });
  }

  // Entry level lines (sampled)
  const levelStep = Math.max(1, Math.floor(entryLevels.length / 30));
  for (let i = 0; i < entryLevels.length; i += levelStep) {
    shapes.push({
      type: 'line',
      xref: 'paper',
      x0: 0,
      x1: 0.85,
      yref: 'y',
      y0: entryLevels[i],
      y1: entryLevels[i],
      line: {
        color: localTradingMode.value === 'LONG' ? '#3b82f6' : '#f97316',
        width: 1,
        dash: 'solid',
      },
      opacity: 0.5,
    });
  }

  // Period range shading
  if (stats.period_low && stats.period_high) {
    shapes.push({
      type: 'rect',
      xref: 'paper',
      x0: 0,
      x1: 0.85,
      yref: 'y',
      y0: stats.period_low,
      y1: stats.period_high,
      fillcolor: localTradingMode.value === 'LONG'
        ? 'rgba(34, 197, 94, 0.1)'
        : 'rgba(239, 68, 68, 0.1)',
      line: { width: 0 },
    });
  }

  // Y-axis range
  const dataHigh = Math.max(...highs);
  const dataLow = Math.min(...lows);
  const gridHigh = config.range_ceiling_price || dataHigh;
  const gridLow = config.range_floor_price || dataLow;
  const overallHigh = Math.max(dataHigh, gridHigh, refPrice || dataHigh);
  const overallLow = Math.min(dataLow, gridLow, refPrice || dataLow);
  const range = overallHigh - overallLow;
  const padding = range * 0.05;

  const layout = {
    title: {
      text: `${apiSymbol.value} Grid (${localTradingMode.value})`,
      font: { size: 13 },
    },
    xaxis: {
      rangeslider: { visible: false },
      type: 'date',
      domain: [0, 0.85],
    },
    xaxis2: {
      title: 'Size',
      domain: [0.88, 1],
      anchor: 'y',
      side: 'top',
    },
    yaxis: {
      title: 'Price',
      side: 'left',
      autorange: false,
      fixedrange: false,
      showgrid: true,
      gridcolor: 'rgba(107, 114, 128, 0.2)',
      range: [overallLow - padding, overallHigh + padding],
    },
    shapes,
    paper_bgcolor: 'transparent',
    plot_bgcolor: 'transparent',
    font: {
      color: '#9ca3af',
    },
    margin: { t: 35, b: 40, l: 60, r: 20 },
    showlegend: false,
    annotations: refPrice ? [
      {
        x: 0.92,
        y: refPrice,
        xref: 'paper',
        yref: 'y',
        text: 'CURRENT',
        showarrow: false,
        font: { size: 9, color: localTradingMode.value === 'LONG' ? '#22c55e' : '#ef4444' },
      },
    ] : [],
  };

  const plotConfig = {
    responsive: true,
    displayModeBar: true,
    modeBarButtonsToRemove: ['lasso2d', 'select2d'],
  };

  Plotly.react(container, traces, layout, plotConfig);
};

// Fullscreen open/close
const openFullscreen = () => {
  maximized.value = true;
  nextTick(() => {
    setTimeout(() => {
      renderChart(fullscreenChartContainer.value);
    }, 100);
  });
};

const closeFullscreen = () => {
  // Purge the fullscreen chart before closing
  if (fullscreenChartContainer.value) {
    Plotly.purge(fullscreenChartContainer.value);
  }
  maximized.value = false;
  // Re-render inline chart after closing
  nextTick(() => {
    setTimeout(() => renderChart(chartContainer.value), 100);
  });
};

// Exposed method for parent components to trigger chart refresh
// (e.g., when BotModal switches to the Strategy tab)
const refreshChart = () => {
  if (resultData.value) {
    setTimeout(() => {
      const container = getActiveChartContainer();
      if (container && container.offsetWidth > 0 && container.offsetHeight > 0) {
        renderChart(container);
      }
    }, 50);
  }
};

defineExpose({ refreshChart, resultData });

// Copy config to clipboard
const copyConfig = async () => {
  if (!resultData.value?.grid_config) return;
  try {
    await navigator.clipboard.writeText(JSON.stringify(resultData.value.grid_config, null, 2));
  } catch (err) {
    console.error('Failed to copy:', err);
  }
};

// Resize handler
const handleResize = () => {
  const container = getActiveChartContainer();
  if (resultData.value && container) {
    Plotly.Plots.resize(container);
  }
};

// Setup ResizeObserver to detect when chart container becomes visible
// (handles v-show tab switching and modal animation)
const setupResizeObserver = () => {
  if (resizeObserver) resizeObserver.disconnect();

  resizeObserver = new ResizeObserver((entries) => {
    for (const entry of entries) {
      const { width, height } = entry.contentRect;
      if (width > 0 && height > 0 && pendingRender.value && resultData.value) {
        renderChart(entry.target);
      }
    }
  });

  if (chartContainer.value) {
    resizeObserver.observe(chartContainer.value);
  }
};

// Initialize parameters from defaults prop (e.g. from saved _range_explorer in strategy_config)
watch(() => props.defaults, (val) => {
  if (!val) return;
  if (val.total_levels) totalLevels.value = val.total_levels;
  if (val.profit_ratio) profitRatio.value = val.profit_ratio;
  if (val.range_multiplier) rangeMultiplier.value = val.range_multiplier;
  if (val.capital_usd) capitalUSD.value = val.capital_usd;
  if (val.lookback_period) lookbackPeriod.value = val.lookback_period;
  if (val.timeframe) timeframe.value = val.timeframe;
  if (val.sizing_mode) sizingMode.value = val.sizing_mode;
  if (val.max_order_size_multiplier) maxOrderSizeMultiplier.value = val.max_order_size_multiplier;
  if (val.spacing_factor) spacingFactor.value = val.spacing_factor;
  if (val.geometric_scaling_curve) geometricScalingCurve.value = val.geometric_scaling_curve;
}, { immediate: true });

// Sync tradingMode prop changes
watch(() => props.tradingMode, (val) => {
  if (val) {
    localTradingMode.value = val.toUpperCase();
  }
});

// Watch chartContainer ref to setup ResizeObserver when the container appears in DOM
watch(chartContainer, (newEl) => {
  if (newEl && resizeObserver) {
    resizeObserver.observe(newEl);
    // If we have pending data, attempt to render
    if (pendingRender.value && resultData.value) {
      setTimeout(() => renderChart(newEl), 100);
    }
  }
});

// Watch collapse toggle to re-render chart (Issue #5)
watch(collapsed, (newVal) => {
  if (!newVal && resultData.value) {
    nextTick(() => {
      setTimeout(() => {
        if (chartContainer.value) {
          Plotly.Plots.resize(chartContainer.value);
        }
      }, 50);
    });
  }
});

// Heavy params: need fresh OHLC data — show loading spinner
watch(
  [apiSymbol, localTradingMode, lookbackPeriod, timeframe],
  () => {
    debouncedFetch(true);
  },
);

// Light params: only grid math changes — keep chart visible, no spinner
watch(
  [totalLevels, profitRatio, rangeMultiplier, capitalUSD, sizingMode, maxOrderSizeMultiplier, spacingFactor, geometricScalingCurve],
  () => {
    debouncedFetch(false);
  },
);

// Lifecycle
onMounted(() => {
  window.addEventListener('resize', handleResize);
  setupResizeObserver();
  if (apiSymbol.value) {
    // Delay initial fetch so modal DOM is ready
    setTimeout(() => {
      fetchCalculation();
    }, 100);
  }
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize);
  if (debounceTimer) clearTimeout(debounceTimer);
  if (resizeObserver) resizeObserver.disconnect();
  if (chartContainer.value) {
    Plotly.purge(chartContainer.value);
  }
  if (fullscreenChartContainer.value) {
    Plotly.purge(fullscreenChartContainer.value);
  }
});
</script>

<style scoped>
.chart-container {
  width: 100%;
  height: 400px;
  min-height: 400px;
}

.fullscreen-chart-container {
  width: 100%;
  min-height: 300px;
}

.modal-backdrop {
  background: rgba(0, 0, 0, 0.5);
}
</style>
